import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { type ScanResult, useScannerSync } from './sync.js';
import { ensureDeviceId, KEYS, storage } from './storage.js';
import { createDecoder, type QrDecoder } from './decoder.js';

const SCAN_INTERVAL_MS = 220;
const OVERLAY_AUTO_CLEAR_MS = 2200;
const SCAN_COOLDOWN_MS = 900;

type Overlay = { kind: 'idle' } | { kind: 'scanning' } | { kind: 'result'; result: ScanResult };

/**
 * /:lang/scan/:eventId
 *
 * Reads the scanner token from the URL fragment (`#token=…`) on first load
 * and stores it in localStorage scoped to the eventId. Subsequent loads
 * pick it up from storage automatically — the operator only needs to be
 * configured once per phone+event pair.
 */
export function ScanPage() {
  const { lang, eventId } = useParams<{ lang: string; eventId: string }>();
  const { t, i18n } = useTranslation('scanner');

  const [token, setToken] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<Overlay>({ kind: 'idle' });
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [permissionState, setPermissionState] = useState<'requesting' | 'granted' | 'denied'>(
    'requesting',
  );

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const decoderRef = useRef<QrDecoder | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cooldownRef = useRef<number>(0);
  const lastPayloadRef = useRef<string | null>(null);
  const autoClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const busyRef = useRef(false);

  const load = useScannerSync((s) => s.load);
  const pull = useScannerSync((s) => s.pullBundle);
  const validate = useScannerSync((s) => s.validate);
  const flush = useScannerSync((s) => s.flushPending);
  const resetSession = useScannerSync((s) => s.resetSession);
  const bundle = useScannerSync((s) => s.bundle);
  const session = useScannerSync((s) => s.session);
  const pending = useScannerSync((s) => s.pending);
  const isOnline = useScannerSync((s) => s.isOnline);
  const isSyncing = useScannerSync((s) => s.isSyncing);
  const lastSyncAt = useScannerSync((s) => s.lastSyncAt);

  // Bootstrap: parse the token from the URL fragment, hydrate storage,
  // refresh the bundle, flush any leftover pending validations.
  useEffect(() => {
    if (!eventId) return;
    const fragment = window.location.hash;
    const m = fragment.match(/[#&]token=([^&]+)/);
    let tok = storage.get<string>(KEYS.scannerToken(eventId));
    if (m?.[1]) {
      tok = decodeURIComponent(m[1]);
      storage.set(KEYS.scannerToken(eventId), tok);
      // Strip the token from the URL so it doesn't sit in browser history
      // or leak through screenshots of the address bar.
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', cleanUrl);
    }
    setToken(tok);
    setDeviceId(ensureDeviceId());
    load(eventId, tok);
    if (tok) {
      void (async () => {
        try {
          await pull(eventId, tok);
          await flush(eventId, tok);
        } catch {
          /* offline ok */
        }
      })();
    }
  }, [eventId, load, pull, flush]);

  // Online/offline event listeners. When the device comes back online, try
  // to flush the queue immediately.
  useEffect(() => {
    if (!eventId || !token) return;
    const onOnline = () => {
      void (async () => {
        try {
          await pull(eventId, token);
          await flush(eventId, token);
        } catch {
          /* stays offline */
        }
      })();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [eventId, token, pull, flush]);

  // Auto-clear the result overlay.
  useEffect(() => {
    if (overlay.kind !== 'result') return;
    autoClearRef.current = setTimeout(() => {
      setOverlay({ kind: 'idle' });
    }, OVERLAY_AUTO_CLEAR_MS);
    return () => {
      if (autoClearRef.current) clearTimeout(autoClearRef.current);
    };
  }, [overlay]);

  // Camera + decoder boot. We start as soon as we know we have a token.
  useEffect(() => {
    if (!token || !eventId || !deviceId) return;
    let cancelled = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: 1280, height: 720 },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setPermissionState('granted');

        const decoder = await createDecoder();
        if (cancelled) {
          decoder.dispose();
          return;
        }
        decoderRef.current = decoder;

        tickRef.current = setInterval(() => {
          void scanOnce(deviceId);
        }, SCAN_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : '';
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          setPermissionState('denied');
        } else {
          setCameraError(err instanceof Error ? err.message : 'Camera unavailable');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (tickRef.current) clearInterval(tickRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (decoderRef.current) decoderRef.current.dispose();
      streamRef.current = null;
      decoderRef.current = null;
      tickRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, eventId, deviceId]);

  async function scanOnce(devId: string) {
    if (busyRef.current) return;
    if (overlay.kind === 'result') return;
    const video = videoRef.current;
    const decoder = decoderRef.current;
    if (!video || !decoder) return;
    busyRef.current = true;
    try {
      const payload = await decoder.detect(video);
      if (!payload) return;
      const now = Date.now();
      if (payload === lastPayloadRef.current && now - cooldownRef.current < SCAN_COOLDOWN_MS)
        return;
      lastPayloadRef.current = payload;
      cooldownRef.current = now;
      setOverlay({ kind: 'scanning' });
      const r = await validate(payload, devId);
      void buzz(r.result);
      setOverlay({ kind: 'result', result: r });
    } finally {
      busyRef.current = false;
    }
  }

  // ---------- Render branches ----------

  if (!eventId) {
    return <FullScreen text={t('scan.loading_event')} />;
  }

  if (!token) {
    return (
      <FullScreen>
        <h1 className="text-2xl font-bold text-neutral-100 mb-3 text-center">
          {t('no_token.title')}
        </h1>
        <p className="text-sm text-neutral-400 max-w-sm text-center mb-6">{t('no_token.body')}</p>
        <Link to={`/${lang ?? 'es'}`} className="btn-secondary text-sm">
          {t('no_token.back_home')}
        </Link>
      </FullScreen>
    );
  }

  if (permissionState === 'denied') {
    return (
      <FullScreen>
        <h1 className="text-2xl font-bold text-neutral-100 mb-2">
          {t('permission.camera_required_title')}
        </h1>
        <p className="text-sm text-neutral-400 max-w-xs text-center mb-6">
          {t('permission.denied')}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="btn-primary text-sm"
        >
          {t('permission.grant')}
        </button>
      </FullScreen>
    );
  }

  if (cameraError) {
    return (
      <FullScreen>
        <h1 className="text-2xl font-bold text-red-300 mb-2">{t('scan.invalid_reason.unknown')}</h1>
        <p className="text-sm text-neutral-400 max-w-xs text-center">{cameraError}</p>
      </FullScreen>
    );
  }

  const totalScans = session.valid + session.duplicates + session.invalid;
  const eventTitle = bundle?.eventTitle ?? '';
  const progress = bundle
    ? { validated: bundle.totalValidated + session.valid, total: bundle.totalIssued }
    : null;

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Top header */}
      <div className="absolute top-0 left-0 right-0 px-3 pt-[max(env(safe-area-inset-top),12px)] pb-3 bg-gradient-to-b from-black/70 to-transparent">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white text-2xl leading-none"
            aria-label={t('sync.exit_event')}
          >
            ×
          </button>
          <div className="flex-1 min-w-0 text-center">
            {eventTitle && (
              <p className="text-white font-semibold text-sm truncate">{eventTitle}</p>
            )}
            <div className="flex items-center justify-center gap-1.5 text-xs text-white/90">
              <span
                className={[
                  'w-2 h-2 rounded-full',
                  isOnline ? 'bg-emerald-400' : 'bg-amber-400',
                ].join(' ')}
              />
              <span>{isOnline ? t('status.online') : t('status.offline')}</span>
              {pending.length > 0 && (
                <span className="text-amber-300">
                  · {t('status.pending_uploads', { count: pending.length })}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!eventId || !token) return;
              void (async () => {
                try {
                  await pull(eventId, token);
                  await flush(eventId, token);
                } catch {
                  /* offline */
                }
              })();
            }}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white text-lg leading-none"
            aria-label={t('sync.force_sync')}
          >
            {isSyncing ? '⟳' : '↻'}
          </button>
        </div>
        {progress && (
          <div className="mt-3 flex justify-center">
            <span className="text-xs font-semibold text-[var(--color-accent)] bg-[var(--color-accent)]/15 border border-[var(--color-accent)]/40 rounded-full px-3 py-1">
              {t('stats.progress', { validated: progress.validated, total: progress.total })}
            </span>
          </div>
        )}
      </div>

      {/* Reticle */}
      {overlay.kind === 'idle' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="w-[68vmin] h-[68vmin] max-w-[280px] max-h-[280px] border-[3px] border-white/70 rounded-3xl" />
          <p
            className="mt-4 text-sm text-white"
            style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}
          >
            {t('scan.aim_qr')}
          </p>
        </div>
      )}

      {/* Bottom session bar */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pt-4 pb-[max(env(safe-area-inset-bottom),20px)] bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <SessionStat label={t('session.valid')} value={session.valid} color="#22C55E" />
          <SessionStat label={t('session.duplicates')} value={session.duplicates} color="#F59E0B" />
          <SessionStat label={t('session.invalid')} value={session.invalid} color="#EF4444" />
          <button
            type="button"
            onClick={() => {
              if (eventId) resetSession(eventId);
            }}
            className="text-xs text-white/70"
          >
            {t('session.total_scans', { count: totalScans })} · {t('session.reset')}
          </button>
        </div>
      </div>

      {overlay.kind === 'result' && (
        <ResultOverlay
          result={overlay.result}
          lang={i18n.language}
          lastSyncAt={lastSyncAt}
          onDismiss={() => setOverlay({ kind: 'idle' })}
        />
      )}
    </div>
  );
}

function SessionStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex flex-col items-center min-w-[56px]">
      <span className="text-2xl font-extrabold leading-none" style={{ color }}>
        {value}
      </span>
      <span className="text-[10px] uppercase tracking-widest text-white/60 mt-1">{label}</span>
    </div>
  );
}

function ResultOverlay({
  result,
  lang,
  lastSyncAt,
  onDismiss,
}: {
  result: ScanResult;
  lang: string;
  lastSyncAt: string | null;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('scanner');
  const colors = colorsFor(result.result);
  const ticketLine = result.ticket
    ? `${result.ticket.name}${result.ticket.kind ? ` · ${result.ticket.kind}` : ''}`
    : null;
  const titleKey =
    result.result === 'valid'
      ? 'scan.valid_title'
      : result.result === 'duplicate'
        ? 'scan.duplicate_title'
        : 'scan.invalid_title';
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(lang, {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short',
      }),
    [lang],
  );
  const firstAt = result.firstValidatedAt
    ? dateFmt.format(new Date(result.firstValidatedAt))
    : null;
  return (
    <button
      type="button"
      onClick={onDismiss}
      className="absolute inset-0 flex flex-col items-center justify-center px-6 py-10 text-center"
      style={{ backgroundColor: colors.bg, color: colors.fg }}
    >
      <div
        className="w-24 h-24 rounded-full border-4 flex items-center justify-center mb-6"
        style={{ borderColor: colors.fg }}
      >
        <span className="text-6xl font-black leading-none">
          {result.result === 'valid' ? '✓' : result.result === 'duplicate' ? '!' : '×'}
        </span>
      </div>
      <h2 className="text-4xl font-black tracking-tight mb-2">{t(titleKey)}</h2>
      {ticketLine && <p className="text-xl font-bold mb-4 break-words max-w-md">{ticketLine}</p>}
      {result.result !== 'invalid' && typeof result.scanCount === 'number' && (
        <span
          className="border-2 rounded-full px-4 py-1 text-sm font-bold mb-4"
          style={{ borderColor: colors.fg }}
        >
          {t('scan.scan_count_short', { count: result.scanCount })}
        </span>
      )}
      {result.result === 'duplicate' && (
        <>
          <p className="text-sm opacity-90">{t('scan.duplicate_subtitle')}</p>
          {firstAt && (
            <p className="text-sm opacity-90 mt-1">
              {t('scan.first_validated_at', { time: firstAt })}
            </p>
          )}
        </>
      )}
      {result.result === 'invalid' && result.reason && (
        <p className="text-sm opacity-90 max-w-md">
          {t(`scan.invalid_reason.${result.reason}` as 'scan.invalid_reason.unknown')}
        </p>
      )}
      <p className="text-xs mt-5 opacity-85">
        {result.source === 'online' ? t('scan.online_confirmed') : t('scan.offline_pending')}
      </p>
      <p className="text-xs mt-6 opacity-70">{t('scan.tap_to_continue')}</p>
      {lastSyncAt && (
        <p className="text-[11px] mt-1 opacity-50">
          {t('status.synced_at', { time: dateFmt.format(new Date(lastSyncAt)) })}
        </p>
      )}
    </button>
  );
}

function FullScreen({ text, children }: { text?: string; children?: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-neutral-950 flex flex-col items-center justify-center p-6">
      {children}
      {text && <p className="text-sm text-neutral-400">{text}</p>}
    </div>
  );
}

function colorsFor(kind: 'valid' | 'duplicate' | 'invalid'): { bg: string; fg: string } {
  if (kind === 'valid') return { bg: '#16A34A', fg: '#04210E' };
  if (kind === 'duplicate') return { bg: '#F59E0B', fg: '#3B1F02' };
  return { bg: '#DC2626', fg: '#3A0606' };
}

async function buzz(kind: 'valid' | 'duplicate' | 'invalid'): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  if (kind === 'valid') navigator.vibrate(70);
  else if (kind === 'duplicate') navigator.vibrate([60, 60, 60]);
  else navigator.vibrate([120, 80, 120, 80, 120]);
}
